/**************************************************
 * 
 * Global QMod Component Changes
 * 
 * These are temporary hotfixes until QMod makes official updates.
 * 
 **************************************************/

/**
 * Function to delay execution by a given time in milliseconds.
 */
function delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Centralized function to apply all QMod accessibility fixes.
 * Called on initial load and after browser resize.
 * 
 * @since CT-784
 */
async function applyAllQmodAccessibilityFixes() {
    jq('[id^=highcharts-].highcharts-container').attr("role", "region");
    assignUniqueAriaLabels();
    assignUniqueFilterIds();
    jq('[id^=highcharts-end-of-chart-marker-]').attr("role", "region");
    
    await Promise.all([
        assignUniqueAttributes(),
        fixEmptyTableHeaders(),
        addTabIndexToVolumeToggle(),
        fixInvalidAriaAttributes(),
        assignUniqueLabels()
    ]);
    
    setAriaLabelsForIcons();
}

/**
 * Debounce utility to prevent excessive function calls during resize.
 * 
 * @since CT-784
 */
function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

/**
 * Reapply QMod accessibility fixes on browser resize.
 * Uses debounce to wait until resize is complete.
 * 
 * @since CT-784
 */
const reapplyQmodFixesOnResize = debounce(function() {
    applyAllQmodAccessibilityFixes();
}, 250);

window.addEventListener('resize', reapplyQmodFixesOnResize);

/**
 * Page Type: QMod - Chart, Quote & Historical 
 * 
 * Assigns unique aria-labels to highcharts containers.
 * 
 * @since QMod v1.69.0
 */
function assignUniqueAriaLabels() {
    const charts = jq('[id^=highcharts-].highcharts-container');
    charts.each(function (index) {
        const uniqueLabel = `Chart Container ${index + 1}`;
        jq(this).attr("aria-label", uniqueLabel);
    });
}

/**
 * Page Type: QMod - Chart, Quote & Historical 
 * 
 * Assigns unique IDs to duplicate filters within SVG defs.
 * 
 * @since QMod v1.69.0
 */
function assignUniqueFilterIds() {
    const filters = jq('svg defs filter[id="highcharts-drop-shadow-0"]');
    filters.each(function (index) {
        if (index > 0) {
            const newId = `highcharts-drop-shadow-${Math.random().toString(36).substr(2, 9)}`;
            jq(this).attr('id', newId);

            // Update any references to this filter id in the SVG
            const references = jq(`svg [filter="url(#highcharts-drop-shadow-0)"]`);
            references.each(function () {
                jq(this).attr('filter', `url(#${newId})`);
            });
        }
    });
}

// Call toggleTabIndex and apply all accessibility fixes once QMod content is ready
observeForContent('.highcharts-container').then(() => {
    toggleTabIndex(null, true);
    applyAllQmodAccessibilityFixes();
});

/**
 * Page Type: QMod - Chart, Quote & Historical 
 * 
 * Fixes the unique ID and missing aria attributes.
 * 
 * @since QMod v1.69.0
 */
function assignUniqueAttributes() {
    return new Promise((resolve) => {
        function generateUniqueId(baseId) {
            return baseId + '-' + Math.random().toString(36).substr(2, 9);
        }

        const ids = [
            'highcharts-end-of-chart-marker-0',
            'highcharts-screen-reader-region-before-0',
            'highcharts-drop-shadow-0',
            'highcharts-screen-reader-region-after-0',
            'highcharts-2ih7fo0-0',
            'highcharts-fjy84ra-0'
        ];

        ids.forEach((baseId) => {
            const elements = document.querySelectorAll(`#${baseId}`);
            elements.forEach((element, index) => {
                if (index > 0) {
                    const newId = generateUniqueId(baseId);
                    element.setAttribute('id', newId);
                }
            });
        });

        const landmarkElements = document.querySelectorAll('.highcharts-exit-anchor, .highcharts-container');
        landmarkElements.forEach((element, index) => {
            if (!element.getAttribute('aria-label')) {
                element.setAttribute('aria-label', `Unique Chart ${index + 1}`);
            }
        });

        const filters = document.querySelectorAll('svg defs filter[id="highcharts-drop-shadow-0"]');
        filters.forEach((filter, index) => {
            if (index > 0) {
                const newId = generateUniqueId('highcharts-drop-shadow-0');
                filter.setAttribute('id', newId);

                const references = document.querySelectorAll(`svg [filter="url(#highcharts-drop-shadow-0)"]`);
                references.forEach((reference) => {
                    jq(reference).attr('filter', `url(#${newId})`);
                });
            }
        });

        const specificElements = ['highcharts-3yiiuel-0', 'highcharts-pu44erb-0'];
        specificElements.forEach((baseId, index) => {
            const element = document.querySelector(`#${baseId}`);
            if (element && !element.getAttribute('aria-label')) {
                element.setAttribute('aria-label', `Unique Chart Specific ${index + 1}`);
            }
        });

        resolve();
    });
}

// Call the function after the DOM has fully loaded
document.addEventListener('DOMContentLoaded', () => {
    assignUniqueAttributes();
});

/**
 * Page Type: QMod - Historical Data
 * 
 * Creates and inserts a label for an input selected by a specific CSS selector.
 * 
 * @since QMod v1.69.0
 */
function createLabelForInputBySelector(selector, labelText) {
    let inputElement = document.querySelector(selector);
    if (!inputElement) {
        return;
    }

    let inputId = 'input-' + Math.random().toString(36).substr(2, 9);
    inputElement.id = inputId;

    let label = document.createElement('label');
    label.htmlFor = inputId;
    label.textContent = labelText;
    label.classList.add("visually-hidden");

    inputElement.parentNode.insertBefore(label, inputElement);
}

// Create and insert labels using the specific QMod selectors after delay
delay(2000).then(() => {
    createLabelForInputBySelector('#qmod-fromdate > div > div.duet-date__input-wrapper > input.duet-date__input', 'From date');
    createLabelForInputBySelector('#qmod-todate > div > div.duet-date__input-wrapper > input.duet-date__input', 'To date');
});

/**
 * Page Type: QMod - Historical Data
 * 
 * Adds a span element with class "visually-hidden" to all matching th elements.
 * 
 * @since QMod v1.69.0
 */
function addSrOnlySpanToThElements() {
    // Select all th QMod elements with the specified class
    let thElements = document.querySelectorAll('th.qmod-control.control.sorting_disabled.dtr-hidden');

    // Iterate over each th element
    thElements.forEach(function(th) {
        // Check if there's already an visually-hidden span, if not, add one
        if (!th.querySelector('.visually-hidden')) {
            let span = document.createElement('span');
            span.className = 'visually-hidden';
            span.textContent = 'Control Header';
            span.setAttribute('aria-hidden', 'true');
    
            th.appendChild(span);
        }
    });
}

// Call the SR Only Function after delay
delay(2000).then(() => {
    addSrOnlySpanToThElements();
});

/**
 * Observes for content and resolves the promise once content is found.
 */
function observeForContent(selector) {
    return new Promise((resolve, reject) => {
        let checkInterval = setInterval(() => {
            if (jq(selector).html()) {
                clearInterval(checkInterval);
                resolve();
            }
        }, 100); // Check every 100 milliseconds
    });
}

/**
 * Page Type: QMod - Historical Data
 * 
 * QMOD table header SR issue fix for sorting headers.
 * 
 * @since QMod v1.69.0
 */
if (jq('body').hasClass('page-type-qmod---historical-data')) {
    observeForContent('#qmod-price-history').then(() => {
        // Append visually-hidden caption to table
        let $historyTable = jq('#qmod-price-history');
        $historyTable.prepend('<caption class="visually-hidden">Column headers are sortable.</caption>');
        
        // Remove aria-labels on sorting headers
        $historyTable.find('th.sorting').each(function() {
            jq(this).removeAttr('aria-label');
            // Remove aria-labels again on click, as they get added by qmod
            jq(this).accessibleClick(function (e) {
                jq('#qmod-price-history th.sorting').removeAttr('aria-label');
            });
        });
    });
}

/**
 * Global for all QMod Components with Legends
 * 
 * Sets tabindex and accessible click for .qmod-legend li elements.
 */
delay(1000).then(() => {
    jq('.qmod-legend li').each(function(i) {
        jq(this).attr('tabindex', 0);
        jq(this).accessibleClick(function(evt) {
            // We want to use the accessibleClick method so that the logic surrounding
            // keys is consistent; However, avoid recursion by causing an infinite click loop
            if (evt.type != 'click') {
                jq(this).click();
                return false;
            }
        });
    });
});

/**
 * Global for all QMod Components with Main Chart
 * 
 * Fixes the unique label for qmod-main-chart.
 */
function assignUniqueLabels() {
    return new Promise((resolve) => {
        const elements = document.querySelectorAll('.qmod-main-chart');

        elements.forEach((element, index) => {
            if (!element.getAttribute('aria-label')) {
                element.setAttribute('aria-label', `Unique Chart ${index + 1}`);
            }
        });

        resolve();
    });
}

// Call the function after the DOM has fully loaded
document.addEventListener('DOMContentLoaded', () => {
    assignUniqueLabels();
});

/**
 * Page Type: QMod - Dividends
 * 
 * Fixes invalid ARIA attribute names.
 * 
 * @since QMod v1.69.0
 */
function fixInvalidAriaAttributes() {
    return new Promise((resolve) => {
        document.querySelectorAll('[aria-role]').forEach(element => {
            element.removeAttribute('aria-role');
        });

        resolve();
    });
}

/**
 * Page Type: QMod - Chart, Quote & Historical 
 * 
 * Toggles the tabindex and aria-hidden attributes of elements in the tab panels 
 * to prevent tabbing into hidden fields.
 * 
 * @since QMod v1.69.0
 */
function toggleTabIndex(event, isFirstLoad = false) {
    let next, previous;
    if (!isFirstLoad) {
        next = jq(event.target.hash);
        previous = jq(event.relatedTarget.hash);
    } else {
        next = jq('#tab_quote');
        previous = jq('#tab_historical');
    }

    // Add aria-hidden="true" and tabindex="-1" to the hidden tab-panel
    previous.attr('aria-hidden', 'true');
    previous.find('input, button:not(.no-tab), th.sorting, .qmod-calendar-control, .highcharts-container, .qmod-a11y-toggle-wrap, .qmod-a11y-dropdown2-combobox, .highcharts-exit-anchor').attr('tabindex', '-1');
    previous.find('.highcharts-exit-anchor').attr('aria-hidden', 'true');

    // Remove aria-hidden and set tabindex="0" from the active tab-panel
    next.removeAttr('aria-hidden');
    next.find('input, button:not(.no-tab), th.sorting, .qmod-calendar-control, .highcharts-container, .qmod-a11y-toggle-wrap, .qmod-a11y-dropdown2-combobox, .highcharts-exit-anchor').attr('tabindex', '0');
    next.find('.highcharts-exit-anchor').attr('aria-hidden', 'false');
}

// Update table inputs on tab click
jq('.stock-tab a[data-bs-toggle="tab"]').on('shown.bs.tab', function (event) {
    toggleTabIndex(event, false);
});

// Initial load adjustments
delay(2000).then(() => {
    toggleTabIndex(null, true);
});

/**
 * Ensure dropdown in historical data pane opens on Enter key press
 */
jq(document).on('keydown', '.qmod-a11y-dropdown2', function (event) {
    if (event.key === 'Enter') {
        let expanded = jq(this).attr('aria-expanded') === 'true';
        jq(this).attr('aria-expanded', !expanded);
        jq(this).click();
    }
});

/**
 * Page Type: QMod - Chart, Quote & Historical 
 * 
 * Ensures that down and up arrow icons are not hidden and have the appropriate aria-labels.
 */
function setAriaLabelsForIcons() {
    const downArrowIcon = jq('.qmod-change.qmod-ch-down .qmod-change-icon.fa.fa-arrow-circle-down');
    const upArrowIcon = jq('.qmod-change.qmod-ch-up .qmod-change-icon.fa.fa-arrow-circle-up');

    if (downArrowIcon.length > 0) {
        downArrowIcon.attr('aria-hidden', 'false');
        downArrowIcon.attr('aria-label', 'Down');
        downArrowIcon.attr('role', 'img');
        downArrowIcon.removeAttr('aria-disabled');
    }

    if (upArrowIcon.length > 0) {
        upArrowIcon.attr('aria-hidden', 'false');
        upArrowIcon.attr('aria-label', 'Up');
        upArrowIcon.attr('role', 'img');
        upArrowIcon.removeAttr('aria-disabled');
    }
}

// Call the function with a delay after the DOM has fully loaded
document.addEventListener('DOMContentLoaded', () => {
    setTimeout(() => {
        setAriaLabelsForIcons();
    }, 2000); // 2-second delay
});

/**
 * Page Type: QMod - Dividends
 *
 * Sets aria-labels on dividend table pagination controls, removes the invalid
 * aria-role attribute QMod adds, and sets the valid role="link" attribute required
 * for aria-label on DataTables paginate controls rendered as href-less <a> elements.
 */
function setDividendsPaginationAriaLabels(pagination) {
    const prevButton = pagination.querySelector('.previous');
    const nextButton = pagination.querySelector('.next');

    if (prevButton) {
        prevButton.setAttribute('aria-label', 'Previous Page');
    }

    if (nextButton) {
        nextButton.setAttribute('aria-label', 'Next Page');
    }

    pagination.querySelectorAll('.paginate_button').forEach(button => {
        // QMod adds invalid aria-role="link"; use the valid role attribute instead.
        button.removeAttribute('aria-role');

        // DataTables paginate controls are often <a> without href, so they have no implicit
        // link role; role="link" is required for aria-label / aria-current (axe aria-prohibited-attr).
        button.setAttribute('role', 'link');

        if (button.classList.contains('current')) {
            button.setAttribute('aria-current', 'page');
        } else {
            button.removeAttribute('aria-current');
        }

        if (!button.classList.contains('previous') && !button.classList.contains('next')) {
            const blabel = parseInt(button.getAttribute('data-dt-idx'), 10) + 1;
            button.setAttribute('aria-label', 'Page ' + blabel);
        }
    });

    pagination.querySelectorAll('.ellipsis').forEach(ellipsis => {
        if (!ellipsis.querySelector('.visually-hidden')) {
            const srOnlySpan = document.createElement('span');
            srOnlySpan.className = 'visually-hidden';
            srOnlySpan.textContent = 'Ellipsis indicating non-visible pages';
            ellipsis.appendChild(srOnlySpan);
        }
    });
}

function wrapDividendsPaginationInNav(pagination) {
    if (pagination.closest('.pagination-wrapper')) {
        return;
    }

    const navWrapper = document.createElement('nav');
    navWrapper.classList.add('pagination-wrapper');
    navWrapper.setAttribute('aria-label', 'Pagination');
    pagination.parentNode.insertBefore(navWrapper, pagination);
    navWrapper.appendChild(pagination);
}

function addDividendsTableAccessibleNames() {
    const upcomingTable = document.querySelector('#DataTables_Table_0');
    const upcomingHeading = document.querySelector('.qmod-upcoming-dividends .qmod-heading');

    if (upcomingTable && upcomingHeading && !upcomingTable.hasAttribute('aria-labelledby')) {
        const headingId = 'upcoming-dividends-heading';
        upcomingHeading.setAttribute('id', headingId);
        upcomingTable.setAttribute('aria-labelledby', headingId);
    }

    const historyTable = document.querySelector('#DataTables_Table_1');
    const dividendHistoryHeading = document.querySelector('.qmod-dividends-history .qmod-heading');

    if (historyTable && dividendHistoryHeading && !historyTable.hasAttribute('aria-labelledby')) {
        const headingId = 'dividend-history-heading';
        dividendHistoryHeading.setAttribute('id', headingId);
        historyTable.setAttribute('aria-labelledby', headingId);
    }
}

/**
 * Page Type: QMod - Dividends
 *
 * Wraps pagination in a nav element and sets appropriate aria-labels for both
 * upcoming and history dividend tables.
 */
function enhancePaginationAccessibility() {
    const paginations = document.querySelectorAll('.dividends-data [id$="_paginate"]');

    if (!paginations.length) {
        return;
    }

    addDividendsTableAccessibleNames();

    paginations.forEach(pagination => {
        wrapDividendsPaginationInNav(pagination);
        setDividendsPaginationAriaLabels(pagination);

        if (pagination.dataset.a11yObserverAttached) {
            return;
        }

        pagination.dataset.a11yObserverAttached = 'true';

        const observer = new MutationObserver(() => {
            setDividendsPaginationAriaLabels(pagination);
            addDividendsTableAccessibleNames();
        });
        observer.observe(pagination, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['aria-role']
        });
    });

    const historyPagination = document.querySelector('#DataTables_Table_1_paginate');

    if (!historyPagination || historyPagination.dataset.a11yScrollAttached) {
        return;
    }

    historyPagination.dataset.a11yScrollAttached = 'true';

    let currentPage = null;
    let isScrolling = false;

    function getCurrentPage() {
        const currentButton = historyPagination.querySelector('.paginate_button.current');
        return currentButton ? currentButton.textContent.trim() : null;
    }

    currentPage = getCurrentPage();

    function scrollToTableTop() {
        if (isScrolling) {
            return;
        }

        isScrolling = true;
        const newPage = getCurrentPage();

        if (newPage !== currentPage) {
            currentPage = newPage;

            const dividendsTable = document.querySelector('.dividends-data');
            if (dividendsTable) {
                dividendsTable.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });

                setTimeout(() => {
                    try {
                        const dividendHistoryHeading = document.querySelector('.qmod-dividends-history .qmod-heading');
                        if (dividendHistoryHeading) {
                            dividendHistoryHeading.setAttribute('tabindex', '-1');
                            dividendHistoryHeading.focus();
                        }
                    } finally {
                        isScrolling = false;
                    }
                }, 300);
            } else {
                isScrolling = false;
            }
        } else {
            isScrolling = false;
        }
    }

    historyPagination.addEventListener('click', (e) => {
        const button = e.target.closest('.paginate_button:not(.disabled)');
        if (button && !button.classList.contains('current')) {
            setTimeout(scrollToTableTop, 200);
        }
    });

    historyPagination.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            const button = e.target.closest('.paginate_button:not(.disabled)');
            if (button && !button.classList.contains('current')) {
                setTimeout(scrollToTableTop, 200);
            }
        }
    });
}

if (document.querySelector('.dividends-data')) {
    observeForContent('.dividends-data .dataTables_paginate').then(() => {
        fixInvalidAriaAttributes();
        enhancePaginationAccessibility();
    });
}

/**
 * Page Type: QMod - Chart, Quote & Historical 
 * 
 * Adds meaningful aria-label to empty table headers.
 * 
 * @since QMod v1.69.0
 */
function fixEmptyTableHeaders() {
    return new Promise((resolve) => {
        const emptyHeaders = document.querySelectorAll('th.qmod-control.control.sorting_disabled[aria-label=""]');
        emptyHeaders.forEach(header => {
            header.setAttribute('aria-label', 'Empty header');
        });
        resolve();
    });
}

// Call the function after the DOM has fully loaded
document.addEventListener('DOMContentLoaded', () => {
    fixEmptyTableHeaders();
});

/**
 * Page Type: QMod - Chart, Quote & Historical 
 * 
 * Adds a valid role to .qmod-a11y-toggle-wrap.qmod-volume-toggle elements.
 * 
 * @since QMod v1.69.0
 */
function addTabIndexToVolumeToggle() {
    return new Promise((resolve) => {
        const toggleWrapElements = document.querySelectorAll('.qmod-a11y-toggle-wrap.qmod-volume-toggle');
        toggleWrapElements.forEach(element => {
            element.setAttribute('tabindex', '-1');
        });
        resolve();
    });
}

// Call the function after the DOM has fully loaded
document.addEventListener('DOMContentLoaded', () => {
    addTabIndexToVolumeToggle();
});

/**
 * Page Type: QMod - Chart, Quote & Historical
 *
 * Removes link behavior from QuoteMedia Highcharts credits.
 * Credit text stays visible. Runs again when charts redraw.
 *
 * @since CT-886
 */
function disableHighchartsCreditsLinks() {
    jq('text.highcharts-credits').each(function () {
        var $credit = jq(this);
        var creditText = $credit.text();
        var $link = $credit.parent('a');

        if (
            $credit.attr('role') === 'img'
            && $credit.attr('aria-label') === creditText
            && $credit.css('cursor') === 'default'
            && $credit.css('pointer-events') === 'none'
            && !$credit.attr('onclick')
            && !$link.length
        ) {
            return;
        }

        $credit
            .attr('role', 'img')
            .attr('aria-label', creditText)
            .css({
                cursor: 'default',
                'pointer-events': 'none'
            })
            .off('click')
            .removeAttr('onclick');

        $link.each(function () {
            jq(this).replaceWith(jq(this).children());
        });
    });
}

if (
    jq('body').is('.page-type-qmod---quote, .page-type-qmod---chart, .page-type-qmod---historical-data')
) {
    observeForContent('.highcharts-credits').then(function () {
        disableHighchartsCreditsLinks();

        if (document.body.dataset.highchartsCreditsObserverAttached) {
            return;
        }

        document.body.dataset.highchartsCreditsObserverAttached = 'true';

        var creditsObserver = new MutationObserver(function (mutations) {
            var needsUpdate = false;

            mutations.forEach(function (mutation) {
                if (needsUpdate) {
                    return;
                }

                if (
                    mutation.type === 'attributes'
                    && mutation.target
                    && mutation.target.matches
                    && mutation.target.matches('text.highcharts-credits')
                ) {
                    needsUpdate = true;
                    return;
                }

                Array.prototype.forEach.call(mutation.addedNodes, function (node) {
                    if (needsUpdate || node.nodeType !== 1) {
                        return;
                    }

                    if (
                        (node.matches && node.matches('.highcharts-credits'))
                        || (node.querySelector && node.querySelector('.highcharts-credits'))
                    ) {
                        needsUpdate = true;
                    }
                });
            });

            if (needsUpdate) {
                disableHighchartsCreditsLinks();
            }
        });

        creditsObserver.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'onclick', 'aria-label']
        });
    });
}